/**
 * IT / MDM downloads on web Settings: the silent pkg and the uninstaller.
 * Hidden on desktop (those users already have the app and an Uninstall
 * button) and when signed out.
 */

import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { usePlatform } from '@localagent/platform';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import type { DownloadManifest } from './DownloadForMac';
import { desktopDownloadsUrl } from '../boot/deployment-keys';

function formatSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '';
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ItInstallerSection() {
  const platform = usePlatform();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const [manifest, setManifest] = useState<DownloadManifest | null>(null);

  useEffect(() => {
    if (platform.type === 'desktop' || !isAuthenticated) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch(`${desktopDownloadsUrl()}/manifest.json`, { cache: 'no-store' });
        if (!resp.ok) return;
        const body = (await resp.json()) as DownloadManifest;
        if (!cancelled) setManifest(body);
      } catch {
        if (!cancelled) setManifest({ placeholder: true, reason: 'manifest fetch failed' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [platform.type, isAuthenticated]);

  if (platform.type === 'desktop' || !isAuthenticated) {
    return null;
  }

  if (!manifest || manifest.placeholder) {
    return null;
  }

  const pkgHref = manifest.latest_pkg;
  const uninstallHref = manifest.latest_uninstall;
  if (!pkgHref || !uninstallHref) {
    throw new Error('download manifest is live but lacks latest_pkg or latest_uninstall');
  }
  const pkgMeta = formatSize(manifest.pkg_size_bytes);

  return (
    <section className="space-y-4" data-testid="settings-it-installer">
      <div>
        <h2 className="text-lg font-semibold">IT / MDM</h2>
        <p className="text-sm text-muted-foreground">
          Silent all-users installer for device management. Consumers should use
          the Download Mac app link (DMG) instead.
        </p>
      </div>
      <div className="rounded-lg bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Installer package</p>
            {pkgMeta && <p className="text-xs text-muted-foreground">{pkgMeta}</p>}
          </div>
          <a
            data-testid="settings-it-pkg"
            href={pkgHref}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Download .pkg
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3">
          <div>
            <p className="text-sm font-medium">Uninstaller</p>
            <p className="text-xs text-muted-foreground">
              Removes the app and LaunchAgents. macOS may require right-click →
              Open the first time.
            </p>
          </div>
          <a
            data-testid="settings-it-uninstall"
            href={uninstallHref}
            className="inline-flex h-9 items-center justify-center rounded-md bg-secondary px-3 text-sm font-medium text-secondary-foreground hover:bg-secondary/80"
          >
            Download uninstaller
          </a>
        </div>
      </div>
    </section>
  );
}
